import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import utilStyles from "../../asset/styles/util/util.module.scss"
import styles from "../../asset/styles/transportation/dayTrip.module.scss"
import Grid from "@mui/material/Grid"
import Button from "component/button/button"
import Popup from "../../component/popup/popup"
import IconsColor from "../../util/iconsColor"
import DayTripConfiguration from "./dayTripConfiguration"
import DayTripSeatingPricing from "./dayTripSeatingPricing"
import RichTextEditor from "../../component/richTextEditor/RichTextEditor"
import configStyles from "../../asset/styles/dayTripDetail/dayTripDetail.module.scss"
import {
    DayTripDetailModel,
    getEmptyDayTrip,
} from "./dayTripDetailTypes"
import { modelToPutBody, apiRowToModel } from "./dayTripDetailApiMapper"
import { agent } from "apiClient/api"
import { ExcursionData, PlaceData, SelectOption } from "../../models"
import { useSnackbar } from "../../context/snackbarContext"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { toTitleCase } from "util/textcase"
import { getActiveCityOptions } from "util/cityOptionsUtil"

export interface DayTripValidationErrors {
    trip_name?: string
    city?: string
    start_point?: string
    end_point?: string
    stops?: string
    seating_pricing?: string
    sic_price_per_person?: string
}

function validateDayTrip(data: DayTripDetailModel): DayTripValidationErrors {
    const errors: DayTripValidationErrors = {}
    if (!(data.trip_name ?? "").trim()) {
        errors.trip_name = "Trip Name is required"
    }
    if (data.city_id == null) {
        errors.city = "City is required"
    }
    if (!(data.start_point_id ?? "").trim()) {
        errors.start_point = "Start Point is required"
    }
    if (!(data.end_point_id ?? "").trim()) {
        errors.end_point = "End Point is required"
    }

    const stops = data.stops ?? []
    if (stops.length > 0) {
        const emptyStopIndex = stops.findIndex((s) => !(s?.stop_id ?? "").trim())
        if (emptyStopIndex !== -1) {
            errors.stops = "Stop is required"
        }
        const startId = (data.start_point_id ?? "").trim()
        const endId = (data.end_point_id ?? "").trim()
        const firstStopId = (stops[0]?.stop_id ?? "").trim()
        const lastStopId = (stops[stops.length - 1]?.stop_id ?? "").trim()
        if (firstStopId && startId === firstStopId) {
            errors.start_point = "Start point cannot be the same as the first stop"
        }
        if (lastStopId && endId === lastStopId) {
            errors.end_point = "End point cannot be the same as the last stop"
        }
        if (!errors.stops) {
            for (let i = 0; i < stops.length - 1; i++) {
                const a = (stops[i]?.stop_id ?? "").trim()
                const b = (stops[i + 1]?.stop_id ?? "").trim()
                if (a && b && a === b) {
                    errors.stops = "No two consecutive stops can be the same"
                    break
                }
            }
        }
    }
    const seatingPrices = data.seating_prices ?? []
    const hasInvalidSeatingPrice = seatingPrices.some(
        (p) => p.price_per_vehicle == null || !Number.isFinite(p.price_per_vehicle) || p.price_per_vehicle < 0
    )
    if (seatingPrices.length > 0 && hasInvalidSeatingPrice) {
        errors.seating_pricing = "All seating prices are required and must be greater than 0"
    }
    if (data.flag_enable_sic) {
        const sic = data.sic_price_per_person
        if (sic == null || !Number.isFinite(sic) || sic < 0) {
            errors.sic_price_per_person = "Required"
        }
    }
    return errors
}

function hasValidationErrors(errors: DayTripValidationErrors): boolean {
    return Object.keys(errors).length > 0
}

const DayTripDetail: React.FC = () => {
    const { day_trip_id } = useParams<{ day_trip_id: string }>()
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbar()
    const { hotelGenericData, setHotelGenericData } = useHotelGeneric()
    const [data, setData] = useState<DayTripDetailModel | null>(null)
    const [validationErrors, setValidationErrors] =
        useState<DayTripValidationErrors>({})
    const [excursions, setExcursions] = useState<ExcursionData[]>([])
    const [places, setPlaces] = useState<PlaceData[]>([])
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [inactiveErrorData, setInactiveErrorData] = useState<{ name: string; message: string }[] | null>(null)
    const tripTypeOptions: SelectOption[] = (hotelGenericData?.day_trip_type ?? []).map((item) => ({
        value: String(item.day_trip_type_id),
        label: item.day_trip_type_name,
    }))
    const cityOptions: SelectOption[] = getActiveCityOptions(hotelGenericData?.city, data?.city_id)

    useEffect(() => {
        const fetchGeneric = async () => {
            try {
                const response = await agent.GenericGlobalAPI.get()
                if (response?.status !== false && response?.data?.[0]) {
                    setHotelGenericData(response.data[0])
                }
            } catch (e) {
                console.error("DayTripDetail fetch generic error", e)
            }
        }
        fetchGeneric()
    }, [setHotelGenericData])

    useEffect(() => {
        if (day_trip_id === "new" || !day_trip_id) {
            setData(getEmptyDayTrip(hotelGenericData?.seating_type))
        } else {
            setLoadingDetail(true)
            const seating_type = hotelGenericData?.seating_type
            agent.DayTripDetailAPI.get(day_trip_id)
                .then((res) => {
                    if (res?.status === true && res?.data?.[0]) {
                        setData(apiRowToModel(res.data[0], seating_type))
                    } else {
                        setData(getEmptyDayTrip(seating_type))
                        if (res?.status === false && res?.error) {
                            showError(res.error?.message ?? "Failed to load day trip")
                        }
                    }
                })
                .catch((e) => {
                    console.error("DayTripDetail fetch error", e)
                    setData(getEmptyDayTrip(seating_type))
                    showError("Failed to load day trip")
                })
                .finally(() => setLoadingDetail(false))
        }
    }, [day_trip_id, showError, hotelGenericData?.seating_type])

    useEffect(() => {
        if (day_trip_id === "new" || !day_trip_id) {
            setData(getEmptyDayTrip(hotelGenericData?.seating_type))
        }
    }, [day_trip_id, hotelGenericData?.seating_type])

    useEffect(() => {
        const fetchExcursionsAndPlaces = async () => {
            try {
                const [excRes, placeRes] = await Promise.all([
                    agent.ExcursionsListAPI.get(true),
                    agent.PlaceListAPI.get(true),
                ])
                if (excRes?.status === true && excRes?.data) {
                    setExcursions(Array.isArray(excRes.data) ? excRes.data : [])
                }
                if (placeRes?.status === true && placeRes?.data) {
                    setPlaces(Array.isArray(placeRes.data) ? placeRes.data : [])
                }
            } catch (e) {
                console.error("DayTripDetail fetch excursions/places error", e)
            }
        }
        fetchExcursionsAndPlaces()
    }, [])

    const updateData = (updates: Partial<DayTripDetailModel>) => {
        setData((prev) => (prev ? { ...prev, ...updates } : null))
    }

    const handleBack = () => {
        navigate(-1)
    }

    // const handleCancel = () => {
    //     navigate(-1)
    // }

    const handleSave = async () => {
        if (!data) return
        const errors = validateDayTrip(data)
        setValidationErrors(errors)
        if (hasValidationErrors(errors)) return
        const body = modelToPutBody({ ...data, trip_name: data.trip_name?.trim() }, hotelGenericData?.seating_type)
        try {
            const res = await agent.DayTripPutAPI.put(body)
            if (res?.status === false) {
                if (res?.error?.code === 400 && Array.isArray(res?.data) && res.data.length > 0) {
                    setInactiveErrorData(
                        res.data.map((item: { name?: string; message?: string }) => ({
                            name: item.name ?? "",
                            message: item.message ?? "",
                        }))
                    )
                } else {
                    showError(res?.error?.message ?? "Failed to save day trip")
                }
                return
            }
            showSuccess("Day trip saved successfully")
            if (res?.data?.[0]) {
                setData(apiRowToModel(res.data[0], hotelGenericData?.seating_type))
                setValidationErrors({})
                if (day_trip_id === "new" && res.data[0].day_trip_id) {
                    navigate(`/transportation/day-trip/${res.data[0].day_trip_id}`, { replace: true })
                }
            }
        } catch (e) {
            console.error("DayTripDetail save error", e)
            showError("Failed to save day trip")
        }
    }

    if (!data || loadingDetail) {
        return (
            <div className={utilStyles.pageContainer}>
                <div style={{ textAlign: "center", padding: 48 }}>Loading...</div>
            </div>
        )
    }

    return (
        <div className={utilStyles.pageContainer}>
            <Popup
                isOpen={!!inactiveErrorData}
                onClose={() => setInactiveErrorData(null)}
                title="Cannot update Day trip"
                primaryButton={{ label: "OK", onClick: () => setInactiveErrorData(null) }}
                heightFiexd={true}
            >
                {inactiveErrorData && inactiveErrorData.length > 0 ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                        <p className={styles.confirmationText}>The following day trips are missing or inactive:</p>
                        <div className={styles.dayTripUsageWarning}>
                            <ul className={styles.dayTripUsageList}>
                                {inactiveErrorData.map((item, index) => (
                                    <li key={index}>
                                        <strong>{item.name}</strong> — {item.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : null}
            </Popup>
            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 8 }}>
                    <h1 className={utilStyles.header}>
                        {day_trip_id === "new" ? "Add Day Trip" : toTitleCase(data.trip_name) || "Day Trip Detail"}
                    </h1>
                </Grid>
                <Grid
                    size={{ xs: 12, md: 4 }}
                    style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
                >
                    <Button
                        type="outlined"
                        label="← Back"
                        style={{ marginRight: "16px" }}
                        onClick={handleBack}
                    />
                    <Button type="contained" label="Save" onClick={handleSave} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <DayTripConfiguration
                                data={data}
                                excursions={excursions}
                                places={places}
                                tripTypeOptions={tripTypeOptions}
                                cityOptions={cityOptions}
                                onChange={updateData}
                                errors={{
                                    trip_name: validationErrors.trip_name,
                                    city: validationErrors.city,
                                    start_point: validationErrors.start_point,
                                    end_point: validationErrors.end_point,
                                    stops: validationErrors.stops,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <DayTripSeatingPricing
                                data={data}
                                onChange={updateData}
                                error={validationErrors.seating_pricing}
                                sicPriceError={validationErrors.sic_price_per_person}
                            />
                            <div className={utilStyles.pageContainerContent} style={{ marginTop: 16 }}>
                                <h2 style={{ marginBottom: "24px",  }} className={configStyles.itineraryHeading}>Itinerary</h2>
                                <RichTextEditor
                                    value={data.day_trip_itinerary}
                                    onChange={(html) => updateData({ day_trip_itinerary: html })}
                                    placeholder="Write the itinerary for this trip…"
                                    maxHeightPx={260}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    )
}

export default DayTripDetail
